import React from "react";
import { Page, Text, View, Document, StyleSheet, Image } from "@react-pdf/renderer";
import logo from "../../images/57logo.jpeg";

const styles = StyleSheet.create({
  page: { padding: 20, fontFamily: "Helvetica" },
  header: { flexDirection: "row", justifyContent: "space-between", marginBottom: 10 },
  title: { fontSize: 18, fontWeight: "bold" },
  subtitle: { fontSize: 10, color: "#666" },
  logo: { width: 80, height: 60 },
  siteInfo: { marginVertical: 10, padding: 8, backgroundColor: "#f0f0f0", borderRadius: 5 },
  infoRow: { flexDirection: "row", marginBottom: 3 },
  infoLabel: { width: 100, fontWeight: "bold", fontSize: 10 },
  infoValue: { fontSize: 10 },

  table: { borderWidth: 1, borderColor: "#000", marginTop: 5 },
  tableHeader: { flexDirection: "row", backgroundColor: "#112131" },
  tableHeaderCell: {
    padding: 4,
    fontSize: 10,
    fontWeight: "bold",
    color: "#fff",
    borderRightWidth: 1,
    borderColor: "#000",
    textAlign: "center",
  },

  groupRow: { flexDirection: "row" },

  mergedCell: {
    borderRightWidth: 1,
    borderColor: "#000",
    justifyContent: "center",
    alignItems: "center",
  },
  mergedText: { fontSize: 10, textAlign: "center" },

  subCol: { 
    flexDirection: "column", 
    borderRightWidth: 1, 
    borderColor: "#000", 
    flex: 1,
  },
  subCell: {
    fontSize: 10,
    padding: 4,
    borderBottomWidth: 1,
    borderColor: "#000",
    textAlign: "center",
    minHeight: 20, // Minimum height for consistency
    justifyContent: "center",
    flexGrow: 1, // Allow cells to grow to match height
  },
  lastSubCell: {
    fontSize: 10,
    padding: 4,
    textAlign: "center",
    minHeight: 20, // Minimum height for consistency
    justifyContent: "center",
    flexGrow: 1, // Allow cells to grow to match height
  },
  subCellContent: {
    textAlign: "center",
    width: "100%",
  },
});

const columnFlex = {
  index: 0.5,
  date: 1,
  staff: 1.5,
  time: 1,
  subject: 1.5,
  occurrence: 2,
};

const EoccurrenceReportPDF = ({ eoccurrences = [], siteDetails = null }) => {
  const currentDate = new Date();
  const formattedDate = `${String(currentDate.getDate()).padStart(2, "0")}/${String(
    currentDate.getMonth() + 1
  ).padStart(2, "0")}/${currentDate.getFullYear()}`;

  const getStaff = (occ) => {
    const u = Array.isArray(occ.userDetails) ? occ.userDetails[0] : occ.userDetails;
    if (!u) return occ.userId || "-";
    return `${u.firstName || ""} ${u.lastName || ""}`.trim() || occ.userId || "-";
  };

  const getDateKey = (occ) => {
    if (!occ?.date) return "-";
    const d = new Date(occ.date);
    return d.toLocaleDateString("en-GB"); // dd/mm/yyyy
  };

  // Calculate content height requirements for each group
  const calculateGroupHeights = (groupedRows) => {
    return groupedRows.map(group => {
      const heights = group.rows.map(row => {
        // Calculate approximate height based on content length
        const timeHeight = Math.ceil((row.time || "-").length / 30) * 12; // Approximate lines * line height
        const subjectHeight = Math.ceil((row.subject || "-").length / 25) * 12;
        const occurrenceHeight = Math.ceil((row.occurrence || "-").length / 20) * 12;
        
        return Math.max(timeHeight, subjectHeight, occurrenceHeight, 20); // Minimum 20
      });
      
      return {
        ...group,
        rowHeights: heights,
        maxHeight: Math.max(...heights),
      };
    });
  };

  // group rows by date+staff
  const groupedRows = [];
  for (let i = 0; i < eoccurrences.length; ) {
    const dateKey = getDateKey(eoccurrences[i]);
    const staffKey = getStaff(eoccurrences[i]);

    let j = i + 1;
    while (
      j < eoccurrences.length &&
      getDateKey(eoccurrences[j]) === dateKey &&
      getStaff(eoccurrences[j]) === staffKey
    ) {
      j++;
    }

    groupedRows.push({
      date: dateKey,
      staff: staffKey,
      rows: eoccurrences.slice(i, j),
    });

    i = j;
  }

  // Calculate heights for all groups
  const groupedRowsWithHeights = calculateGroupHeights(groupedRows);

  let serialCounter = 1;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View>
            <Text style={styles.title}>E-Occurrence Report</Text>
            <Text style={styles.subtitle}>Generated on: {formattedDate}</Text>
          </View>
          <Image src={logo} style={styles.logo} />
        </View>

        {/* Site Info */}
        {siteDetails && (
          <View style={styles.siteInfo}>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Site:</Text>
              <Text style={styles.infoValue}>{siteDetails.name || "-"}</Text>
            </View>
          </View>
        )}

        {/* Table */}
        <View style={styles.table}>
          {/* Header row */}
          <View style={styles.tableHeader}>
            <Text style={[styles.tableHeaderCell, { flex: columnFlex.index }]}>#</Text>
            <Text style={[styles.tableHeaderCell, { flex: columnFlex.date }]}>Date</Text>
            <Text style={[styles.tableHeaderCell, { flex: columnFlex.staff }]}>Staff</Text>
            <Text style={[styles.tableHeaderCell, { flex: columnFlex.time }]}>Time</Text>
            <Text style={[styles.tableHeaderCell, { flex: columnFlex.subject }]}>Subject</Text>
            <Text style={[styles.tableHeaderCell, { flex: columnFlex.occurrence }]}>Occurrence</Text>
          </View>

          {groupedRowsWithHeights.length === 0 && (
            <View style={styles.groupRow}>
              <Text style={[styles.subCell, { flex: 7 }]}>No Data Found</Text>
            </View>
          )}

          {groupedRowsWithHeights.map((group, idx) => {
            const subCount = group.rows.length;
            const totalHeight = group.rowHeights.reduce((sum, height) => sum + height, 0);
            
            return (
              <View style={styles.groupRow} key={idx}>
                {/* Serial numbers */}
                <View style={[styles.subCol, { flex: columnFlex.index }]}>
                  {group.rows.map((r, i2) => (
                    <View
                      key={i2}
                      style={[
                        i2 === subCount - 1 ? styles.lastSubCell : styles.subCell,
                        { height: group.rowHeights[i2] }
                      ]}
                    >
                      <Text style={styles.subCellContent}>{serialCounter++}</Text>
                    </View>
                  ))}
                </View>

                {/* Date (merged) */}
                <View
                  style={[
                    styles.mergedCell,
                    { 
                      flex: columnFlex.date, 
                      height: totalHeight,
                      borderBottomWidth: 1,
                      borderColor: "#000",
                    },
                  ]}
                >
                  <Text style={styles.mergedText}>{group.date}</Text>
                </View>

                {/* Staff (merged) */}
                <View
                  style={[
                    styles.mergedCell,
                    { 
                      flex: columnFlex.staff, 
                      height: totalHeight,
                      borderBottomWidth: 1,
                      borderColor: "#000",
                    },
                  ]}
                >
                  <Text style={styles.mergedText}>{group.staff}</Text>
                </View>

                {/* Time */}
                <View style={[styles.subCol, { flex: columnFlex.time }]}>
                  {group.rows.map((r, i2) => (
                    <View
                      key={i2}
                      style={[
                        i2 === subCount - 1 ? styles.lastSubCell : styles.subCell,
                        { height: group.rowHeights[i2] }
                      ]}
                    >
                      <Text style={styles.subCellContent}>{r.time || "-"}</Text>
                    </View>
                  ))}
                </View>

                {/* Subject */}
                <View style={[styles.subCol, { flex: columnFlex.subject }]}>
                  {group.rows.map((r, i2) => (
                    <View
                      key={i2}
                      style={[
                        i2 === subCount - 1 ? styles.lastSubCell : styles.subCell,
                        { height: group.rowHeights[i2] }
                      ]}
                    >
                      <Text style={styles.subCellContent}>{r.subject || "-"}</Text>
                    </View>
                  ))}
                </View>

                {/* Occurrence */}
                <View style={[styles.subCol, { flex: columnFlex.occurrence }]}>
                  {group.rows.map((r, i2) => (
                    <View
                      key={i2}
                      style={[
                        i2 === subCount - 1 ? styles.lastSubCell : styles.subCell,
                        { height: group.rowHeights[i2] }
                      ]}
                    >
                      <Text style={styles.subCellContent}>{r.occurrence || "-"}</Text>
                    </View>
                  ))}
                </View>
              </View>
            );
          })}
        </View>
      </Page>
    </Document>
  );
};

export default EoccurrenceReportPDF;